import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1171" data-mp="1xqd5bhgodrch">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1172" data-mp="1xsh63qja2a8j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1173" data-mp="22ranvv5lknei">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1174" data-mp="1eybwpne3mmpv">
            <span className="w-8 h-px bg-teal" data-id="element-1175" data-mp="1j1gw3h2vu8aa"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1176" data-mp="1iyvv0uaerwdl">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1177" data-mp="2ufui0ge2sdno"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1178" data-mp="2ui8durgkxtwa">
            Privacy Policy
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1179" data-mp="8qwi2okozn97">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1180" data-mp="3qwiyh75szpeo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-1181" data-mp="3m3gdhnf3doa3">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1182" data-mp="335n9siwn9abn">
            At SyNeural, we are committed to protecting your privacy and
            handling your personal information with care and transparency. This
            Privacy Policy explains how we collect, use, store, and protect your
            information when you use our systems, website, and services.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1183" data-mp="2jm6hw09hp3qe">
            By using SyNeural's services, you agree to the practices described
            in this Privacy Policy. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1184" data-mp="32hwltt0zga6x">
        {/* Information We Collect */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1185" data-mp="37fptyrluje9u">
          <div className="flex items-center gap-4 mb-6" data-id="element-1186" data-mp="1ogrye97wukl1">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1187" data-mp="1jj8kxbj1ebu5">
              <Database size={24} data-id="element-1188" data-mp="1xa5122vs10kb" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1189" data-mp="1ta36sesdf9f2">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6" data-id="element-1190" data-mp="15l1welzz9o75">
            <div data-id="element-1191" data-mp="15m3ehafevayd">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1192" data-mp="10lfieacbs3xa">
                Personal Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1193" data-mp="1elq7b7u2r8ft">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1194" data-mp="2pysmpzkftrye">
                <li data-id="element-1195" data-mp="2l1skjzqpl9ck">Name, email address, and contact information</li>
                <li data-id="element-1196" data-mp="3gb79f0mazjrv">Organization or institutional affiliation</li>
                <li data-id="element-1197" data-mp="3gd0dq99g1rwp">Account credentials and authentication information</li>
                <li data-id="element-1198" data-mp="3lb1w0rz1pn74">Profile information and preferences</li>
                <li data-id="element-1199" data-mp="3q9myky1rsiyq">Communication history and support inquiries</li>
              </ul>
            </div>

            <div data-id="element-1200" data-mp="3v86ikswszkog">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1201" data-mp="3v8q1ocy6wf42">
                Assessment and Platform Data
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1202" data-mp="32kv6hux8088">
                When you use our CJI and NCJI assessment tools, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1203" data-mp="cvi9ebsvr311">
                <li data-id="element-1204" data-mp="htthagzm73gm">Assessment responses and performance data</li>
                <li data-id="element-1205" data-mp="hvebu6lximeh">Reasoning patterns and decision-making metrics</li>
                <li data-id="element-1206" data-mp="mu7ojwte69dl">Development pathway selections and progress tracking</li>
                <li data-id="element-1207" data-mp="qv9rooqfj5ku">
                  Aggregated and anonymized capability data for research
                  purposes
                </li>
              </ul>
            </div>

            <div data-id="element-1208" data-mp="27a5tnnc9297i">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1209" data-mp="279m8zyv7t6ps">
                Technical Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1210" data-mp="22dovs1f90gh8">
                We automatically collect certain technical information,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1211" data-mp="2uzqb21sr8yeo">
                <li data-id="element-1212" data-mp="2q2hsw17lwudf">Device information, browser type, and operating system</li>
                <li data-id="element-1213" data-mp="2l9oo5dc51m1c">IP address and general location data</li>
                <li data-id="element-1214" data-mp="2l83vjt9dwd64">Usage data, page views, and interaction patterns</li>
                <li data-id="element-1215" data-mp="2gb3obf0st4df">Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How We Use Your Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1216" data-mp="38z9am67i31fd">
          <div className="flex items-center gap-4 mb-6" data-id="element-1217" data-mp="xc623n8r5n1a">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1218" data-mp="12b8wmdr3wav0">
              <Eye size={24} data-id="element-1219" data-mp="ndzzt4j1aevw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1220" data-mp="nd6so2g5xnd8">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1221" data-mp="eed32w191x96">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1222" data-mp="jd6cac9i723j">
            <li data-id="element-1223" data-mp="28sdxn4prny3g">To provide, maintain, and improve our systems and services</li>
            <li data-id="element-1224" data-mp="2dpw9b2v64rf5">
              To deliver personalized assessment results, insights, and
              development recommendations
            </li>
            <li data-id="element-1225" data-mp="2nsrpjs7h7ze8">
              To conduct research and analysis to advance our understanding of
              judgment, decision-quality, and human capability
            </li>
            <li data-id="element-1226" data-mp="2wor05mm5aww9">
              To communicate with you about your account, updates, and support
            </li>
            <li data-id="element-1227" data-mp="2rud19a89y7mu">To ensure the security of our systems and prevent fraud or misuse</li>
            <li data-id="element-1228" data-mp="36jsq2ko8w2zw">
              To comply with legal obligations and enforce our terms of service
            </li>
            <li data-id="element-1229" data-mp="31odey0hmgl76">
              To develop new features, tools, and institutional applications
            </li>
          </ul>
        </motion.div>

        {/* Data Sharing and Disclosure */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1230" data-mp="3bl8acailm5h8">
          <div className="flex items-center gap-4 mb-6" data-id="element-1231" data-mp="25p9m2chsvfwb">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1232" data-mp="2apf8ne97qc7j">
              <UserCheck size={24} data-id="element-1233" data-mp="2fignjm9f6br4" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1234" data-mp="2jiit46i9zpbo">
              Data Sharing and Disclosure
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1235" data-mp="1mu2qp9u2hlh5">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1236" data-mp="1vrmvgsr51zwi">
            <li data-id="element-1237" data-mp="20tc3s0mz7zgw">
              <strong data-id="element-1238" data-mp="20tuaxsb6fy3l">With Your Consent:</strong> When you explicitly authorize
              us to share your information
            </li>
            <li data-id="element-1239" data-mp="3c7fwz4lqpjkw">
              <strong data-id="element-1240" data-mp="3c6wqql5gcjmp">Institutional Partners:</strong> If you access SyNeural
              through an educational institution, employer, or government
              program, we may share relevant data with that organization as
              agreed upon
            </li>
            <li data-id="element-1241" data-mp="3hemh60l8lssf">
              <strong data-id="element-1242" data-mp="3clbhqkir8eaj">Service Providers:</strong> With trusted third-party
              vendors who help us operate our platform (e.g., hosting,
              analytics, customer support)
            </li>
            <li data-id="element-1243" data-mp="33klmbhxeopxc">
              <strong data-id="element-1244" data-mp="33l56l5aj3qej">Research Partners:</strong> Aggregated, anonymized data
              may be shared with research institutions for academic and
              scientific purposes
            </li>
            <li data-id="element-1245" data-mp="2tlxm7y5dmvhx">
              <strong data-id="element-1246" data-mp="2tnh1blfar0i4">Legal Requirements:</strong> When required by law, court
              order, or to protect our rights and safety
            </li>
            <li data-id="element-1247" data-mp="rvcg0ssaz1gd">
              <strong data-id="element-1248" data-mp="rvugy2pwu3um">Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </motion.div>

        {/* Data Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1249" data-mp="2e14zmthyz499">
          <div className="flex items-center gap-4 mb-6" data-id="element-1250" data-mp="2j21355yesoe5">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1251" data-mp="1vbeubcgkz033">
              <Lock size={24} data-id="element-1252" data-mp="1qclrcdyx6rh6" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1253" data-mp="1qdwviewa218t">
              Data Security
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1254" data-mp="20ahykuoql2a0">
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1255" data-mp="1cikmr3laea8w">
            <li data-id="element-1256" data-mp="17lkhguw9hbiu">Encryption of data in transit and at rest</li>
            <li data-id="element-1257" data-mp="3j8xoxqy187er">Secure authentication and access controls</li>
            <li data-id="element-1258" data-mp="3j7cz2emnxcn8">Regular security audits and vulnerability assessments</li>
            <li data-id="element-1259" data-mp="3o17eh5v8j5gi">Employee training on data protection and privacy practices</li>
            <li data-id="element-1260" data-mp="3a9sryp24cm4p">
              Incident response procedures and breach notification protocols
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1261" data-mp="3e80anpg57xij">
            While we take reasonable precautions, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot
            guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </motion.div>

        {/* Your Rights and Choices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1262" data-mp="2hjtzsksmo8vt">
          <div className="flex items-center gap-4 mb-6" data-id="element-1263" data-mp="n3vlw29j1bby">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1264" data-mp="n4dpxl2fktuf">
              <Shield size={24} data-id="element-1265" data-mp="ib2jhl1j6f31" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1266" data-mp="ebtl22vjpt15">
              Your Rights and Choices
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1267" data-mp="9dieq24gm0no">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1268" data-mp="3vivuvdx2rt9t">
            <li data-id="element-1269" data-mp="3rhttorjll757">
              <strong data-id="element-1270" data-mp="3ribyickbstoq">Access:</strong> Request a copy of the personal
              information we hold about you
            </li>
            <li data-id="element-1271" data-mp="1tqr0zww0mlwg">
              <strong data-id="element-1272" data-mp="1tr97dmce6gi3">Correction:</strong> Request correction of inaccurate or
              incomplete information
            </li>
            <li data-id="element-1273" data-mp="37o3mwln0gdev">
              <strong data-id="element-1274" data-mp="37ollhox5b3py">Deletion:</strong> Request deletion of your personal
              information (subject to legal obligations)
            </li>
            <li data-id="element-1275" data-mp="3clc5uofzglh6">
              <strong data-id="element-1276" data-mp="3cku0917fkuwl">Portability:</strong> Request a copy of your data in a
              structured, machine-readable format
            </li>
            <li data-id="element-1277" data-mp="3lfqjkmkv7ny6">
              <strong data-id="element-1278" data-mp="3le724snh36ut">Opt-Out:</strong> Opt out of certain data processing
              activities, such as marketing communications
            </li>
            <li data-id="element-1279" data-mp="2toayiwyyi9a2">
              <strong data-id="element-1280" data-mp="2tnss57ikyeof">Withdraw Consent:</strong> Withdraw consent for data
              processing where consent is the legal basis
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1281" data-mp="2xs0g32h7rqll">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1282" data-mp="czx9aph4n8pk">
              privacy@syneural.com
            </a>
            .
          </p>
        </motion.div>

        {/* Data Retention */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1283" data-mp="gjfnm2t2q54a">
          <div className="flex items-center gap-4 mb-6" data-id="element-1284" data-mp="zggvbve0ijbx">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1285" data-mp="zevz81c1usby">
              <FileText size={24} data-id="element-1286" data-mp="uguezd2w0myw" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1287" data-mp="196ryhfpvgohr">
              Data Retention
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1288" data-mp="148i5nbrmdzzb">
            We retain your personal information for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements. Assessment data may be
            retained longer for research and system improvement purposes, but
            will be anonymized where possible. You may request deletion of your
            account and associated data at any time, subject to legal retention
            requirements.
          </p>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1289" data-mp="1rsr3oescnvcm">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1290" data-mp="142wss2rlcgv0">
            Children's Privacy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1291" data-mp="v4bg2o8svw25">
            SyNeural's services are not directed to children under 13 years of
            age. We do not knowingly collect personal information from children
            under 13. If we become aware that we have collected information from
            a child under 13, we will take steps to delete such information. For
            educational use cases involving minors, we work with schools and
            institutions to ensure appropriate parental consent and data
            protection measures are in place.
          </p>
        </motion.div>

        {/* International Data Transfers */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1292" data-mp="3kqwe5oso4rqn">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1293" data-mp="36rdjnyss5ewt">
            International Data Transfers
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1294" data-mp="1vcrjb35tfw64">
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure that such transfers
            comply with applicable data protection laws and implement
            appropriate safeguards, such as standard contractual clauses, to
            protect your information.
          </p>
        </motion.div>

        {/* Changes to This Policy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1295" data-mp="onyln0wkhsx3">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1296" data-mp="13ha2fxdczdgj">
            Changes to This Privacy Policy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1297" data-mp="35b9gurls2axg">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or other
            factors. We will notify you of material changes by posting the
            updated policy on our website and updating the "Last Updated" date.
            Your continued use of our services after such changes constitutes
            acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Contact Us */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1298" data-mp="2v8nkd83x8dvm">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1299" data-mp="2bh8syq01bpmo">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1300" data-mp="3rymuv83mtmgu">
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1301" data-mp="3ayjfyl54b6x">
            <p data-id="element-1302" data-mp="3ci401dtx8ei">
              <strong data-id="element-1303" data-mp="38u84vkww9wm">Email:</strong>{' '}
              <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1304" data-mp="3lh35u5ipu5xi">
                privacy@syneural.com
              </a>
            </p>
            <p data-id="element-1305" data-mp="i0w5o8zieqth">
              <strong data-id="element-1306" data-mp="i0cy9m7hjktp">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1307" data-mp="myo3tkqqz964">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}